import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import type React from "react";

export interface KeySegments {
  before: string;
  match: string;
  after: string;
}

export interface KeyMatchInfo {
  isMatch: boolean;
  isCurrentMatch: boolean;
  /** Tailwind classes to apply to just the matched substring — empty string when there's no match. */
  matchClassName: string;
  /** Split of the key into the part before the match, the matched substring, and the part after — null when there's no match. Render `before + <mark>match</mark> + after` instead of the raw key to highlight only the matched characters. */
  segments: KeySegments | null;
}

/** Splits `key` into the text before/at/after the first case-insensitive occurrence of `query`. Returns null when there's no match. */
const splitKeyByMatch = (key: string, query: string): KeySegments | null => {
  const q = query.trim();
  if (!q) return null;
  const idx = key.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return null;
  return {
    before: key.slice(0, idx),
    match: key.slice(idx, idx + q.length),
    after: key.slice(idx + q.length),
  };
};

interface UseKeySearchOptions {
  /** The data currently being rendered/searched — pass whatever object your JSON renderer is walking. */
  data: any;
  /** Returns the scrollable element containing the rendered JSON. Used to scope querySelectorAll + scrollIntoView. */
  getContainer: () => HTMLElement | null;
  /** Top-level keys to exclude from both rendering and search matching. */
  excludeKeys?: string[];
  /** Whether search should be active at all right now (e.g. false while editing, or for a non-active tab). Defaults to true. */
  enabled?: boolean;
}

const keyMatchesQuery = (key: string, query: string) => {
  const q = query.trim().toLowerCase();
  return q.length > 0 && key.toLowerCase().includes(q);
};

const collectMatchPaths = (
  obj: any,
  query: string,
  excludeKeys: string[],
  prefix: string = "",
  isTop: boolean = true,
): string[] => {
  if (!query || !query.trim()) return [];
  let paths: string[] = [];
  if (Array.isArray(obj)) {
    obj.forEach((item, idx) => {
      const itemPath = prefix ? `${prefix}[${idx}]` : `[${idx}]`;
      paths = paths.concat(
        collectMatchPaths(item, query, excludeKeys, itemPath, false),
      );
    });
  } else if (obj && typeof obj === "object") {
    const keys = Object.keys(obj);
    const filteredKeys = isTop
      ? keys.filter((k) => !excludeKeys.includes(k))
      : keys;
    filteredKeys.forEach((key) => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      if (keyMatchesQuery(key, query)) paths.push(fullPath);
      paths = paths.concat(
        collectMatchPaths(obj[key], query, excludeKeys, fullPath, false),
      );
    });
  }
  return paths;
};

export function useKeySearch({
  data,
  getContainer,
  excludeKeys = [],
  enabled = true,
}: UseKeySearchOptions) {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const matchIndexCounterRef = useRef(0);
  const isMouseOverRef = useRef(false);

  const isSearchExpanded =
    isHovering || isFocused || clickOpen || searchQuery.trim().length > 0;

  const matchCount = useMemo(
    () =>
      enabled ? collectMatchPaths(data, searchQuery, excludeKeys).length : 0,
    [data, searchQuery, enabled],
  );

  const scrollToMatch = useCallback(
    (index: number) => {
      const container = getContainer();
      if (!container) return;
      const els = container.querySelectorAll('[data-search-match="true"]');
      const el = els[index] as HTMLElement | undefined;
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [getContainer],
  );

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery, data]);

  useEffect(() => {
    if (matchCount > 0) scrollToMatch(currentMatchIndex);
  }, [currentMatchIndex, matchCount, scrollToMatch]);

  const goToPrevMatch = useCallback(() => {
    setCurrentMatchIndex((prev) =>
      matchCount === 0 ? 0 : (prev - 1 + matchCount) % matchCount,
    );
  }, [matchCount]);

  const goToNextMatch = useCallback(() => {
    setCurrentMatchIndex((prev) =>
      matchCount === 0 ? 0 : (prev + 1) % matchCount,
    );
  }, [matchCount]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (e.shiftKey) goToPrevMatch();
        else goToNextMatch();
      } else if (e.key === "Escape") {
        setSearchQuery("");
        setClickOpen(false);
        searchInputRef.current?.blur();
      }
    },
    [goToPrevMatch, goToNextMatch],
  );

  const handleSearchIconClick = useCallback(() => {
    if (isSearchExpanded) {
      if (!searchQuery.trim()) setClickOpen(false);
      searchInputRef.current?.focus();
    } else {
      setClickOpen(true);
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isSearchExpanded, searchQuery]);

  // Ctrl/Cmd+F opens & focuses search, scoped to whichever instance the mouse is over.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMouseOverRef.current) return;
      const isFindShortcut =
        (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f";
      if (isFindShortcut) {
        e.preventDefault();
        setClickOpen(true);
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  /** Spread onto whichever outer element should count as "hovering this instance" for the shortcut. */
  const containerMouseHandlers = {
    onMouseEnter: () => {
      isMouseOverRef.current = true;
    },
    onMouseLeave: () => {
      isMouseOverRef.current = false;
    },
  };

  const resetMatchCounter = useCallback(() => {
    matchIndexCounterRef.current = 0;
  }, []);

  /** Call once per rendered key, in tree-walk order, to get match styling + advance the running match index. */
  const getKeyMatchInfo = useCallback(
    (key: string, searchActive: boolean = enabled): KeyMatchInfo => {
      const isMatch = searchActive && keyMatchesQuery(key, searchQuery);
      let isCurrentMatch = false;
      if (isMatch) {
        isCurrentMatch = matchIndexCounterRef.current === currentMatchIndex;
        matchIndexCounterRef.current += 1;
      }
      const matchClassName = isMatch
        ? isCurrentMatch
          ? " text-black rounded px-0.5 ring-2 ring-yellow-600 bg-[var(--chart-11)]"
          : " text-black rounded px-0.5"
        : "";
      const segments = isMatch ? splitKeyByMatch(key, searchQuery) : null;
      return { isMatch, isCurrentMatch, matchClassName, segments };
    },
    [searchQuery, currentMatchIndex, enabled],
  );

  return {
    searchQuery,
    setSearchQuery,
    matchCount,
    currentMatchIndex,
    isSearchExpanded,
    searchInputRef,
    resetMatchCounter,
    getKeyMatchInfo,
    goToPrevMatch,
    goToNextMatch,
    handleSearchKeyDown,
    handleSearchIconClick,
    containerMouseHandlers,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
  };
}
